@import 'common/component';
@import 'forms/reset/input';

:host {
  display: inline-flex;
}

.checkbox {
  display: inline-flex;
  position: relative;
}

.checkbox--disabled {
  opacity: var(--o2-opacity-disabled, #{$opacity-disabled * 1%});
  cursor: not-allowed;

  .checkbox__input {
    pointer-events: none;
  }

  .checkbox__label {
    pointer-events: none;
  }
}

.checkbox__label {
  position: relative;
  display: inline-flex;
  align-items: flex-start;
  color: var(--o2-checkbox-label, #{$black});
  cursor: pointer;
}

.checkbox__input {
  position: relative;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  background-color: var(--o2-checkbox-background, #{$white});
  border: 0.2rem solid var(--o2-checkbox-border, #{$border-primary});
  border-radius: var(--o2-checkbox-border-radius, #{$radius-xs});
  min-width: 2rem;
  width: 2rem;
  height: 2rem;
  margin: 0;
  display: inline-flex;
  cursor: pointer;
  transition-timing-function: var(--o2-transition);
  transition-duration: 0.2s;
  transition-property: all;

  &:hover:not(.checkbox__input--disabled) {
    border-color: var(--o2-checkbox-border-hover, mix($black, $border-primary, $opacity-hover * 1%));
  }

  &:focus {
    border-color: var(--o2-checkbox-border-focus, mix($black, $border-primary, $opacity-focus * 1%));
  }

  &:active {
    border-color: var(--o2-checkbox-border-active, mix($black, $border-primary, $opacity-active * 1%));
  }

  &:focus-visible {
    transition: none;
    transition-timing-function: initial;
    text-decoration: none;
  }

  &:checked:active:focus {
    outline: none;
  }

  &:before {
    width: 2rem;
    height: 2rem;
    background: var(--o2-checkbox-background, #{$white});
    content: '';
    display: inline-block;
    position: absolute;
    top: -1px;
    left: -1px;
    opacity: 0;
  }

  ~ .checkbox__icon {
    position: absolute;
    opacity: 0;
    visibility: hidden;
    margin: $spacing-1;
    pointer-events: none;
  }

  &:checked {
    background-color: var(--o2-checkbox-background-checked, #{$blue-action});
    border: 0.2rem solid var(--o2-checkbox-border-checked, #{$blue-action});
    color: var(--o2-checkbox-color-checked, #{$white});

    &:hover:not(:disabled):not(.checkbox__input--error) {
      background-color: var(--o2-checkbox-background-checked-hover, mix($black, $blue-action, $opacity-hover * 1%));
      border: 0.2rem solid var(--o2-checkbox-border-checked-hover, mix($black, $blue-action, $opacity-hover * 1%));
    }

    &:focus:not(:disabled):not(.checkbox__input--error) {
      background-color: var(--o2-checkbox-background-checked-focus, mix($black, $blue-action, $opacity-focus * 1%));
      border: 0.2rem solid var(--o2-checkbox-border-checked-focus, mix($black, $blue-action, $opacity-focus * 1%));
    }

    &:active:not(:disabled):not(.checkbox__input--error) {
      background-color: var(--o2-checkbox-background-checked-active, mix($black, $blue-action, $opacity-active * 1%));
      border: 0.2rem solid var(--o2-checkbox-border-checked-active, mix($black, $blue-action, $opacity-active * 1%));
    }

    &:before {
      animation: checkbox-do-check 0.15s;
    }

    ~ .checkbox__icon--check {
      visibility: visible;
      opacity: 1;
    }
  }

  &:indeterminate {
    background-color: var(--o2-checkbox-background-indeterminate, #{$blue-action});
    border: 0.2rem solid var(--o2-checkbox-border-indeterminate, #{$blue-action});
    color: var(--o2-checkbox-color-indeterminate, #{$white});

    &:hover:not(:disabled):not(.checkbox__input--error) {
      background-color: var(--o2-checkbox-background-indeterminate-hover, mix($black, $blue-action, $opacity-hover * 1%));
      border: 0.2rem solid var(--o2-checkbox-border-indeterminate-hover, mix($black, $blue-action, $opacity-hover * 1%));
    }

    &:focus:not(:disabled):not(.checkbox__input--error) {
      background-color: var(--o2-checkbox-background-indeterminate-focus, mix($black, $blue-action, $opacity-focus * 1%));
      border: 0.2rem solid var(--o2-checkbox-border-indeterminate-focus, mix($black, $blue-action, $opacity-focus * 1%));
    }

    &:active:not(:disabled):not(.checkbox__input--error) {
      background-color: var(--o2-checkbox-background-indeterminate-active, mix($black, $blue-action, $opacity-active * 1%));
      border: 0.2rem solid var(--o2-checkbox-border-indeterminate-active, mix($black, $blue-action, $opacity-active * 1%));
    }

    &:before {
      animation: checkbox-do-check 0.15s;
    }

    ~ .checkbox__icon--minus {
      visibility: visible;
      opacity: 1;
    }
  }

  &.checkbox__input--error {
    border-color: var(--o2-checkbox-background-error, #{$red});
    background-color: var(--o2-checkbox-background, #{$white});

    &:hover {
      border-color: var(--o2-checkbox-background-error-hover, mix($black, $red, $opacity-hover * 1%));
    }

    &:focus {
      border-color: var(--o2-checkbox-background-error-focus, mix($black, $red, $opacity-focus * 1%));
    }

    &:active {
      border-color: var(--o2-checkbox-background-error-active, mix($black, $red, $opacity-active * 1%));
    }
  }

  &.checkbox__input--error:checked {
    border-color: var(--o2-checkbox-background-error, #{$red});
    background-color: var(--o2-checkbox-background-error-checked, #{$red});

    &:hover {
      border-color: var(--o2-checkbox-background-error-checked-hover, mix($black, $red, $opacity-hover * 1%));
      background-color: var(--o2-checkbox-background-error-checked-hover, mix($black, $red, $opacity-hover * 1%));
    }

    &:focus {
      border-color: var(--o2-checkbox-background-error-checked-focus, mix($black, $red, $opacity-focus * 1%));
      background-color: var(--o2-checkbox-background-error-checked-focus, mix($black, $red, $opacity-focus * 1%));
    }

    &:active {
      border-color: var(--o2-checkbox-background-error-checked-active, mix($black, $red, $opacity-active * 1%));
      background-color: var(--o2-checkbox-background-error-checked-active, mix($black, $red, $opacity-active * 1%));
    }
  }

  &.checkbox__input--error:indeterminate {
    border-color: var(--o2-checkbox-background-error, #{$red});
    background-color: var(--o2-checkbox-background-error-indeterminate, #{$red});

    &:hover {
      border-color: var(--o2-checkbox-background-error-indeterminate-hover, mix($black, $red, $opacity-hover * 1%));
      background-color: var(--o2-checkbox-background-error-indeterminate-hover, mix($black, $red, $opacity-hover * 1%));
    }

    &:focus {
      border-color: var(--o2-checkbox-background-error-indeterminate-focus, mix($black, $red, $opacity-focus * 1%));
      background-color: var(--o2-checkbox-background-error-indeterminate-focus, mix($black, $red, $opacity-focus * 1%));
    }

    &:active {
      border-color: var(--o2-checkbox-background-error-indeterminate-active, mix($black, $red, $opacity-active * 1%));
      background-color: var(--o2-checkbox-background-error-indeterminate-active, mix($black, $red, $opacity-active * 1%));
    }
  }

  &:not(:checked) {
    &:before {
      animation: checkbox-do-uncheck-tick 0.15s;
    }
  }
}

.checkbox__text {
  position: relative;
  font-size: 1.6rem;
  @include font-weight(400);
  line-height: 1.3;
  margin-left: $spacing-3;
}

// Checkbox uncheck tick animation
@keyframes checkbox-do-uncheck-tick {
  0% {
    transform: scale(0);
    opacity: 0;
  }

  25% {
    transform: scale(0.25);
    opacity: 1;
  }

  50% {
    transform: scale(0.5);
    opacity: 1;
  }

  75% {
    transform: scale(0.75);
    opacity: 1;
  }

  100% {
    transform: scale(1);
    opacity: 1;
  }
}

// Checkbox uncheck background animation
@keyframes checkbox-do-uncheck-bg {
  0% {
    background-color: var(--o2-blue-action, #{$blue-action});
  }

  25% {
    background-color: var(--o2-blue-action, #{$blue-action});
  }

  50% {
    background-color: var(--o2-blue-action, #{$blue-action});
  }

  75% {
    background-color: var(--o2-blue-action, #{$blue-action});
  }

  100% {
    background-color:$white;
  }
}

// Checkbox check animation
@keyframes checkbox-do-check {
  0% {
    transform: scale(1);
    opacity: 1;
  }

  25% {
    transform: scale(0.75);
    opacity: 1;
  }

  50% {
    transform: scale(0.5);
    opacity: 1;
  }

  75% {
    transform: scale(0.25);
    opacity: 1;
  }

  100% {
    transform: scale(0);
    opacity: 0;
  }
}
